/**
 * Support Filters – enterprise redesign with pill chips and filter popover
 */
import { useState, useEffect, useRef } from 'react';
import { Input, Button } from '@respectly/ui';
import { Search, X, SlidersHorizontal } from 'lucide-react';
import { cn } from '@respectly/ui';
import type {
  SupportRequestStatus,
  SupportPriority,
  SupportCategory,
} from '@respectly/shared-types';
import { useSupportStore, useActiveFiltersCount } from '../../stores/useSupportStore';

const ALL_VALUE = '__all__';

const statusOptions: { value: SupportRequestStatus | typeof ALL_VALUE; label: string }[] = [
  { value: ALL_VALUE, label: 'All Statuses' },
  { value: 'open', label: 'Open' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'closed', label: 'Closed' },
];

const priorityOptions: { value: SupportPriority | typeof ALL_VALUE; label: string }[] = [
  { value: ALL_VALUE, label: 'All Priorities' },
  { value: 'urgent', label: 'Urgent' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
];

const categoryOptions: { value: SupportCategory | typeof ALL_VALUE; label: string }[] = [
  { value: ALL_VALUE, label: 'All Categories' },
  { value: 'billing', label: 'Billing' },
  { value: 'technical', label: 'Technical' },
  { value: 'compliance', label: 'Compliance' },
  { value: 'account', label: 'Account' },
  { value: 'feature_request', label: 'Feature Request' },
  { value: 'other', label: 'Other' },
];

const dateRangeOptions: { value: typeof ALL_VALUE | '7days' | '30days' | '90days'; label: string }[] = [
  { value: ALL_VALUE, label: 'All Time' },
  { value: '7days', label: 'Last 7 Days' },
  { value: '30days', label: 'Last 30 Days' },
  { value: '90days', label: 'Last 90 Days' },
];

interface SupportFiltersProps {
  className?: string;
  compact?: boolean;
}

export function SupportFilters({ className, compact = false }: SupportFiltersProps) {
  const { filters, setFilter, clearFilters } = useSupportStore();
  const activeFilterCount = useActiveFiltersCount();
  const [searchValue, setSearchValue] = useState(filters.search || '');
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const filterPanelRef = useRef<HTMLDivElement>(null);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchValue !== filters.search) {
        setFilter('search', searchValue || undefined);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchValue, filters.search, setFilter]);

  // Close panel on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (filterPanelRef.current && !filterPanelRef.current.contains(e.target as Node)) {
        setShowFilterPanel(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleClearFilters = () => {
    setSearchValue('');
    clearFilters();
    setShowFilterPanel(false);
  };

  const hasActiveFilters =
    filters.search || filters.status || filters.priority || filters.category ||
    (filters.dateRange && filters.dateRange !== 'all');

  // Build active chips
  const activeChips: { label: string; onRemove: () => void }[] = [];
  if (filters.status) activeChips.push({ label: statusOptions.find((o) => o.value === filters.status)?.label ?? '', onRemove: () => setFilter('status', undefined) });
  if (filters.priority) activeChips.push({ label: priorityOptions.find((o) => o.value === filters.priority)?.label ?? '', onRemove: () => setFilter('priority', undefined) });
  if (filters.category) activeChips.push({ label: categoryOptions.find((o) => o.value === filters.category)?.label ?? '', onRemove: () => setFilter('category', undefined) });
  if (filters.dateRange && filters.dateRange !== 'all') activeChips.push({ label: dateRangeOptions.find((o) => o.value === filters.dateRange)?.label ?? '', onRemove: () => setFilter('dateRange', undefined) });

  return (
    <div className={cn('space-y-2', className)}>
      {/* Search row */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-zinc-400 dark:text-zinc-500" />
          <Input
            placeholder={compact ? 'Search...' : 'Search by subject, description, or tenant…'}
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            className="h-8 pl-9 pr-8 text-sm bg-zinc-50 dark:bg-zinc-800/50 border-zinc-200 dark:border-zinc-700 focus:border-blue-500 focus:ring-blue-500/20 placeholder:text-zinc-400 dark:placeholder:text-zinc-500 rounded-lg"
          />
          {searchValue && (
            <button
              onClick={() => setSearchValue('')}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-zinc-100 dark:hover:bg-zinc-800"
            >
              <X className="h-3 w-3 text-zinc-400" />
            </button>
          )}
        </div>

        {/* Filter button + popover */}
        <div className="relative" ref={filterPanelRef}>
          <button
            type="button"
            onClick={() => setShowFilterPanel(!showFilterPanel)}
            className={cn(
              'h-8 px-3 rounded-lg text-xs font-medium flex items-center gap-1.5 ring-1 transition-colors',
              showFilterPanel || activeFilterCount > 0
                ? 'bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400 ring-blue-500/30'
                : 'bg-zinc-50 dark:bg-zinc-800/50 text-zinc-600 dark:text-zinc-400 ring-zinc-200 dark:ring-zinc-700 hover:bg-zinc-100 dark:hover:bg-zinc-800'
            )}
          >
            <SlidersHorizontal className="h-3.5 w-3.5" />
            {!compact && 'Filters'}
            {activeFilterCount > 0 && (
              <span className="h-4 w-4 rounded-full bg-blue-500 text-white flex items-center justify-center text-[10px] font-bold">
                {activeFilterCount}
              </span>
            )}
          </button>

          {showFilterPanel && (
            <div className="absolute right-0 top-full mt-1.5 w-72 rounded-xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 shadow-xl shadow-black/10 dark:shadow-black/40 z-50 p-4 space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-zinc-700 dark:text-zinc-300 uppercase tracking-wider">Filters</p>
                {hasActiveFilters && (
                  <button onClick={handleClearFilters} className="text-xs text-blue-500 hover:text-blue-600 dark:hover:text-blue-300 font-medium">
                    Clear all
                  </button>
                )}
              </div>

              <FilterGroup label="Status">
                {statusOptions.filter(o => o.value !== ALL_VALUE).map((opt) => (
                  <FilterChip
                    key={opt.value}
                    label={opt.label}
                    isActive={filters.status === opt.value}
                    onClick={() => setFilter('status', filters.status === opt.value ? undefined : opt.value as SupportRequestStatus)}
                  />
                ))}
              </FilterGroup>

              <FilterGroup label="Priority">
                {priorityOptions.filter(o => o.value !== ALL_VALUE).map((opt) => (
                  <FilterChip
                    key={opt.value}
                    label={opt.label}
                    isActive={filters.priority === opt.value}
                    onClick={() => setFilter('priority', filters.priority === opt.value ? undefined : opt.value as SupportPriority)}
                  />
                ))}
              </FilterGroup>

              <FilterGroup label="Category">
                {categoryOptions.filter(o => o.value !== ALL_VALUE).map((opt) => (
                  <FilterChip
                    key={opt.value}
                    label={opt.label}
                    isActive={filters.category === opt.value}
                    onClick={() => setFilter('category', filters.category === opt.value ? undefined : opt.value as SupportCategory)}
                  />
                ))}
              </FilterGroup>

              <FilterGroup label="Date Range">
                {dateRangeOptions.filter(o => o.value !== ALL_VALUE).map((opt) => (
                  <FilterChip
                    key={opt.value}
                    label={opt.label}
                    isActive={filters.dateRange === opt.value}
                    onClick={() => setFilter('dateRange', filters.dateRange === opt.value ? undefined : opt.value as '7days' | '30days' | '90days')}
                  />
                ))}
              </FilterGroup>

              <Button size="sm" className="w-full h-8 text-xs" onClick={() => setShowFilterPanel(false)}>
                Done
              </Button>
            </div>
          )}
        </div>

        {hasActiveFilters && (
          <button
            onClick={handleClearFilters}
            className="h-8 w-8 rounded-lg bg-zinc-100 dark:bg-zinc-800 ring-1 ring-zinc-200 dark:ring-zinc-700 flex items-center justify-center hover:bg-zinc-200 dark:hover:bg-zinc-700 transition-colors"
          >
            <X className="h-3.5 w-3.5 text-zinc-500" />
          </button>
        )}
      </div>

      {/* Active filter chips */}
      {activeChips.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {activeChips.map((chip) => (
            <span
              key={chip.label}
              className="inline-flex items-center gap-1 rounded-full bg-blue-50 dark:bg-blue-950/40 px-2 py-0.5 text-xs font-medium text-blue-700 dark:text-blue-400 ring-1 ring-blue-500/20"
            >
              {chip.label}
              <button onClick={chip.onRemove} className="p-0.5 rounded-full hover:bg-blue-100 dark:hover:bg-blue-900/50">
                <X className="h-2.5 w-2.5" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Helpers ────────────────────────────────────────────────────────────────────

function FilterGroup({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-2">
      <p className="text-[11px] font-semibold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">{label}</p>
      <div className="flex flex-wrap gap-1.5">{children}</div>
    </div>
  );
}

function FilterChip({ label, isActive, onClick }: { label: string; isActive: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'px-2.5 py-1 rounded-full text-xs font-medium ring-1 transition-colors',
        isActive
          ? 'bg-blue-500 text-white ring-blue-500'
          : 'bg-zinc-50 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400 ring-zinc-200 dark:ring-zinc-700 hover:bg-zinc-100 dark:hover:bg-zinc-700'
      )}
    >
      {label}
    </button>
  );
}
